/**
 * Every app-wide provider, composed in one place.
 * App.tsx should never grow a nest of providers again.
 */
import type React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "@features/auth/AuthContext";
import { ModuleDisabledListener } from "@features/auth/ModuleDisabledListener";
import { IdleSessionWatcher } from "@features/auth/IdleSessionWatcher";
import { ToastProvider } from "@shared/ui/Toast/ToastProvider";
import { ErrorBoundary } from "./ErrorBoundary";
import { ApiError } from "@shared/api";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry a permission or validation failure — the answer won't change.
        if (error instanceof ApiError) {
          if (error.isUnauthorized || error.isForbidden || error.isValidation) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

export const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <ModuleDisabledListener />
            <IdleSessionWatcher />
            {children}
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);
